import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import dayjs from "dayjs";

const RecentActivity = () => {
  const orders = useSelector((state) => state.orders.list);

  const [activities, setActivities] = useState([]);

  useEffect(() => {
    // Sort by latest first
    const sorted = [...orders].sort(
      (a, b) => new Date(b.placedAt) - new Date(a.placedAt)
    );

    setActivities(sorted);
  }, [orders]);

  // Calculate remaining days (7 days from order placed)
  const getRemainingDays = (placedAt) => {
    const placed = dayjs(placedAt);
    const expiry = placed.add(7, "day");
    const now = dayjs();
    const diff = expiry.diff(now, "day");
    return diff >= 0 ? diff : 0;
  };

  // Calculate total spending per customer to highlight best customers
  const customerSpending = {};
  orders.forEach((order) => {
    const amt = parseInt(order.amount.replace(/₹|,/g, ""));
    if (customerSpending[order.customer]) {
      customerSpending[order.customer] += amt;
    } else {
      customerSpending[order.customer] = amt;
    }
  });

  const maxSpender = Object.keys(customerSpending).reduce((a, b) =>
    customerSpending[a] > customerSpending[b] ? a : b
  );

  return (
    <div className="p-6 bg-gradient-to-br from-blue-50 to-white dark:from-gray-900 dark:to-gray-800 rounded-2xl shadow-lg">
      <h2 className="text-2xl font-bold mb-6 text-blue-700 dark:text-blue-400">
        Recent Customer Activities
      </h2>

      {activities.length === 0 ? (
        <p className="text-gray-500 dark:text-gray-300">
          No recent activities yet.
        </p>
      ) : (
        <ul className="space-y-4 max-h-[70vh] overflow-y-auto">
          <AnimatePresence>
            {activities.map((order) => {
              const remaining = getRemainingDays(order.placedAt);
              const isBestCustomer = order.customer === maxSpender;

              return (
                <motion.li
                  key={order.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 20 }}
                  transition={{ duration: 0.3 }}
                  className={`flex justify-between items-center p-4 rounded-xl shadow-md transition ${
                    isBestCustomer
                      ? "bg-yellow-50 border-l-4 border-yellow-400"
                      : "bg-white dark:bg-gray-700"
                  } hover:shadow-xl`}
                >
                  <div>
                    <p
                      className={`font-semibold ${
                        isBestCustomer
                          ? "text-yellow-700"
                          : "text-gray-800 dark:text-gray-100"
                      }`}
                    >
                      {order.customer}{" "}
                      {isBestCustomer && (
                        <span className="text-sm font-medium bg-yellow-200 text-yellow-800 px-2 py-0.5 rounded-full ml-2">
                          ⭐ Best Customer
                        </span>
                      )}
                    </p>
                    <p className="text-gray-600 dark:text-gray-300 text-sm">
                      {order.action}
                    </p>
                  </div>

                  <div className="text-right">
                    <p className="text-blue-600 dark:text-blue-400 font-semibold">
                      {order.amount}
                    </p>
                    <p className="text-gray-500 dark:text-gray-400 text-xs">
                      {order.date} • {order.time}
                    </p>
                    <p
                      className={`text-sm font-medium mt-1 ${
                        remaining > 0 ? "text-green-600" : "text-red-600"
                      }`}
                    >
                      {remaining > 0
                        ? `⏳ ${remaining} day(s) left`
                        : "❌ Expired"}
                    </p>
                  </div>
                </motion.li>
              );
            })}
          </AnimatePresence>
        </ul>
      )}
    </div>
  );
};

export default RecentActivity;
